import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Feather, Send, Sparkle, Lock, LockOpen, Trash2, X } from "lucide-react";
import { burst } from "@/lib/confetti";
import { sound } from "@/lib/audio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WishWall() {
    const [wishes, setWishes] = useState([]);
    const [name, setName] = useState("");
    const [message, setMessage] = useState("");
    const [status, setStatus] = useState("idle"); // idle | sending | error
    const [loadFailed, setLoadFailed] = useState(false);
    const [modOpen, setModOpen] = useState(false);
    const [moderating, setModerating] = useState(false);
    const [passcode, setPasscode] = useState("");
    const [modError, setModError] = useState("");
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        fetch(`${API}/wishes`)
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((data) => setWishes(data))
            .catch(() => setLoadFailed(true));
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !message.trim() || status === "sending") return;
        setStatus("sending");
        try {
            const r = await fetch(`${API}/wishes`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: name.trim(), message: message.trim() }),
            });
            if (!r.ok) throw new Error();
            const wish = await r.json();
            setWishes((prev) => [wish, ...prev]);
            setName("");
            setMessage("");
            setStatus("idle");
            sound.chime(1046.5);
            burst({ x: window.innerWidth / 2, y: window.innerHeight * 0.35, count: 60 });
        } catch (err) {
            setStatus("error");
        }
    };

    const unlock = async (e) => {
        e.preventDefault();
        setModError("");
        try {
            const r = await fetch(`${API}/wishes/verify`, {
                method: "POST",
                headers: { "X-Moderation-Passcode": passcode },
            });
            if (r.status === 204) {
                setModerating(true);
                setModOpen(false);
                setModError("");
            } else if (r.status === 401) {
                setModError("Enter the moderation passcode.");
            } else {
                setModError("That passcode is not valid.");
            }
        } catch (err) {
            setModError("The stars are briefly unreachable — try again.");
        }
    };

    const deleteWish = async (id) => {
        if (deletingId) return;
        setDeletingId(id);
        setModError("");
        try {
            const r = await fetch(`${API}/wishes/${encodeURIComponent(id)}`, {
                method: "DELETE",
                headers: { "X-Moderation-Passcode": passcode },
            });
            if (r.status === 204 || r.status === 404) {
                setWishes((prev) => prev.filter((w) => w.id !== id));
                sound.pop();
            } else if (r.status === 401 || r.status === 403) {
                setModerating(false);
                setModOpen(true);
                setModError("Your moderation session expired — enter the passcode again.");
            } else {
                setModError("Delete failed — please try again.");
            }
        } catch (err) {
            setModError("Delete failed — please try again.");
        }
        setDeletingId(null);
    };

    return (
        <section data-testid="wish-wall-section" className="relative z-[1] px-6 py-24 sm:py-32">
            <div className="mx-auto max-w-4xl">
                <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                    <p className="eyebrow-label mb-4">The Wish Wall</p>
                    <h2 className="font-display text-3xl font-semibold leading-snug sm:text-5xl">
                        Notes for Mary, <span className="gold-text italic">kept among the stars</span>
                    </h2>
                    <p className="mx-auto mt-4 max-w-md text-sm font-light text-[#EDE7F8]/75 sm:text-base">
                        Friends and family: leave a birthday message below. Mary can return
                        to this wall and read them anytime.
                    </p>
                    <button
                        data-testid="moderate-button"
                        onClick={() => { setModOpen((o) => !o); setModError(""); }}
                        aria-expanded={modOpen || moderating}
                        className="mx-auto mt-5 flex items-center gap-2 rounded-full border border-[#C4B5FD]/20 px-4 py-1.5 font-mono2 text-[9px] uppercase tracking-[0.25em] text-[#C4B5FD]/50 transition-colors hover:border-[#C4B5FD]/50 hover:text-[#C4B5FD]"
                    >
                        {moderating ? <LockOpen className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                        {moderating ? "Moderating — tap to lock" : "Moderate wishes"}
                    </button>
                    {moderating && (
                        <button
                            data-testid="moderate-lock-button"
                            onClick={() => { setModerating(false); setPasscode(""); }}
                            className="sr-only"
                        >
                            Lock moderation
                        </button>
                    )}
                </motion.div>

                <AnimatePresence>
                    {modOpen && !moderating && (
                        <motion.form
                            data-testid="moderate-form"
                            onSubmit={unlock}
                            className="mx-auto mt-6 flex max-w-sm flex-col items-center gap-3 rounded-xl border border-[#C4B5FD]/25 bg-[#130D24]/90 p-5"
                            initial={{ opacity: 0, y: -12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.35 }}
                        >
                            <label htmlFor="moderate-passcode" className="eyebrow-label">Moderator passcode</label>
                            <div className="flex w-full gap-2">
                                <input
                                    id="moderate-passcode"
                                    data-testid="moderate-passcode-input"
                                    type="password"
                                    autoComplete="off"
                                    value={passcode}
                                    onChange={(e) => setPasscode(e.target.value)}
                                    placeholder="Jerry only"
                                    className="w-full rounded-lg border border-[#C4B5FD]/25 bg-[#0B0716]/70 px-4 py-2.5 text-sm text-[#FBF7EE] placeholder:text-[#C4B5FD]/35 focus:border-[#C4B5FD]/70 focus:outline-none"
                                />
                                <button
                                    data-testid="moderate-unlock-button"
                                    type="submit"
                                    className="shrink-0 rounded-lg border border-[#F3D085]/40 bg-[#F3D085]/10 px-4 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#F3D085] transition-colors hover:bg-[#F3D085]/20"
                                >
                                    Unlock
                                </button>
                            </div>
                        </motion.form>
                    )}
                </AnimatePresence>

                {(moderating || modError) && (
                    <div className="mx-auto mt-4 flex max-w-sm items-center justify-center gap-3 text-center">
                        {moderating && (
                            <p data-testid="moderating-indicator" className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#34D399]">
                                Moderation active — tap a wish's bin to remove it
                            </p>
                        )}
                        {moderating && (
                            <button
                                data-testid="moderate-exit-button"
                                onClick={() => { setModerating(false); setPasscode(""); setModError(""); }}
                                className="text-[#C4B5FD]/60 transition-colors hover:text-[#C4B5FD]"
                                aria-label="Exit moderation"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                )}
                {modError && (
                    <p data-testid="moderate-error" className="mt-3 text-center text-sm text-[#F3D085]">{modError}</p>
                )}

                <motion.form
                    data-testid="wish-form"
                    onSubmit={submit}
                    className="mx-auto mt-12 max-w-xl rounded-2xl border border-[#F3D085]/25 bg-[#160E2A]/75 p-6 backdrop-blur sm:p-8"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                >
                    <label htmlFor="wish-name" className="eyebrow-label mb-2 block">Your name</label>
                    <input
                        id="wish-name"
                        data-testid="wish-name-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={60}
                        required
                        placeholder="e.g. Sarah, Mary's sister"
                        className="w-full rounded-lg border border-[#F3D085]/25 bg-[#0B0716]/70 px-4 py-3 text-sm font-light text-[#FBF7EE] placeholder:text-[#C4B5FD]/35 focus:border-[#F3D085]/70 focus:outline-none"
                    />
                    <label htmlFor="wish-message" className="eyebrow-label mb-2 mt-5 block">Your birthday wish</label>
                    <textarea
                        id="wish-message"
                        data-testid="wish-message-input"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        maxLength={500}
                        required
                        rows={4}
                        placeholder="Write something that will make her smile…"
                        className="w-full resize-none rounded-lg border border-[#F3D085]/25 bg-[#0B0716]/70 px-4 py-3 text-sm font-light text-[#FBF7EE] placeholder:text-[#C4B5FD]/35 focus:border-[#F3D085]/70 focus:outline-none"
                    />
                    <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                        <button data-testid="wish-submit-button" type="submit" disabled={status === "sending"} className="btn-gold disabled:opacity-50">
                            <Send className="h-4 w-4" />
                            {status === "sending" ? "Placing your star…" : "Pin my wish to the wall"}
                        </button>
                        <span className="font-mono2 text-[10px] tracking-[0.2em] text-[#C4B5FD]/50">
                            {500 - message.length} CHARACTERS LEFT
                        </span>
                    </div>
                    {status === "error" && (
                        <p data-testid="wish-error" className="mt-4 text-sm text-[#F3D085]">
                            The stars are briefly unreachable — please try again in a moment.
                        </p>
                    )}
                </motion.form>

                <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <AnimatePresence initial={false}>
                        {wishes.map((w, i) => (
                            <motion.article
                                key={w.id}
                                data-testid={`wish-card-${i}`}
                                className="relative overflow-hidden rounded-2xl border border-[#C4B5FD]/20 bg-[#130D24]/80 p-6"
                                initial={{ opacity: 0, y: 30, scale: 0.97 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.3 } }}
                                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                                layout
                            >
                                <Sparkle className="absolute right-4 top-4 h-4 w-4 text-[#F3D085]/40" />
                                {moderating && (
                                    <button
                                        data-testid={`wish-delete-${i}`}
                                        onClick={() => deleteWish(w.id)}
                                        disabled={deletingId === w.id}
                                        aria-label={`Delete wish from ${w.name}`}
                                        className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border border-red-400/40 bg-red-500/10 text-red-300 transition-colors hover:border-red-400 hover:bg-red-500/25 disabled:opacity-40"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                )}
                                <p className="font-display text-lg font-light italic leading-relaxed text-[#FBF7EE]/95">
                                    "{w.message}"
                                </p>
                                <div className="mt-4 flex items-center gap-2">
                                    <Feather className="h-3.5 w-3.5 text-[#F3D085]/70" />
                                    <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#F3D085]/80">
                                        {w.name}
                                    </p>
                                </div>
                            </motion.article>
                        ))}
                    </AnimatePresence>
                </div>

                {loadFailed && wishes.length === 0 && (
                    <p data-testid="wish-load-error" className="mt-8 text-center text-sm font-light text-[#C4B5FD]/70">
                        The wish wall is resting — refresh the page to try again.
                    </p>
                )}
            </div>
        </section>
    );
}
